import { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { http, fmtEUR } from "@/lib/api";
import { printDriverShiftReport } from "@/lib/driverShiftReport";
import { confirmDialog, promptDialog } from "@/lib/confirmDialog";
import {
  Users as UsersIcon,
  Truck,
  ScanBarcode,
  PlayCircle,
  Square,
  CheckCircle2,
  XCircle,
  Pencil,
  Trash2,
  Check,
  Eye,
  RotateCcw,
  Plus,
  Printer,
} from "lucide-react";

const padCode = (n, w = 4) => String(n ?? 0).padStart(w, "0");

/**
 * Driver Hub — the operator's command-centre for «Οδηγοί Διανομής».
 *
 *   • Top section: list of drivers with an open driver-shift (the only
 *     ones eligible to be scan-assigned). Tap a driver row to drill into
 *     their orders for the current session.
 *   • Middle section: a barcode-scan field. Workflow:
 *       1. Scan one or more 7777XXXX order barcodes — each scan looks
 *          up the DELIVERED order, adds it to the basket and emits a
 *          short toast with the customer + total.
 *       2. If a scanned order is already assigned to another driver,
 *          show a confirm prompt; on accept the order joins the basket
 *          with the «Αλλαγή» flag.
 *       3. Scan an 8888XXXX driver card — assigns the whole basket and
 *          auto-closes the hub.
 *   • Bottom section: admin CRUD (toggle on-shift, edit name/phone,
 *     delete).
 */
export default function DeliveryDriverHub({ isAdmin, onClose, onChanged }) {
  const [drivers, setDrivers] = useState([]);
  const [busy, setBusy] = useState(false);

  // Basket of scanned orders pending driver assignment.
  const [basket, setBasket] = useState([]);
  // Pending «order already assigned to driver X — change?» prompt.
  const [conflictOrder, setConflictOrder] = useState(null);
  // Driver drill-down panel (read-only list of orders).
  const [drillDriver, setDrillDriver] = useState(null);

  // Scan field handling — auto-focus, debounced submit on Enter.
  const scanRef = useRef(null);
  const [scanValue, setScanValue] = useState("");

  // CRUD form (admin only).
  const [form, setForm] = useState({ name: "", phone: "", active: true });
  const [editing, setEditing] = useState(null);

  const reload = useCallback(async () => {
    try {
      const r = await http.get("/drivers");
      setDrivers(r.data || []);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Focus scan input on mount + whenever basket changes (so the
  // operator can keep scanning without manually clicking).
  useEffect(() => {
    const t = setTimeout(() => { try { scanRef.current?.focus(); } catch { /* noop */ } }, 100);
    return () => clearTimeout(t);
  }, [basket, conflictOrder, drillDriver]);

  // -- Driver shift toggle -----------------------------------------
  const toggleShift = async (d) => {
    setBusy(true);
    try {
      if (d.on_shift) {
        await http.post(`/drivers/${d.id}/close-shift`);
        toast.success(`Έκλεισε η βάρδια: ${d.name}`);
      } else {
        await http.post(`/drivers/${d.id}/open-shift`);
        toast.success(`Άνοιξε η βάρδια: ${d.name}`);
      }
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία");
    } finally {
      setBusy(false);
    }
  };

  // -- Barcode handling --------------------------------------------
  const isOrderBarcode = (n) => n >= 77770000 && n < 77780000;
  const isDriverBarcode = (n) => n >= 88880000 && n < 88890000;

  const handleScan = async (raw) => {
    const trimmed = String(raw || "").trim();
    if (!trimmed) return;
    setScanValue("");
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error("Μη έγκυρο barcode");
      return;
    }

    // -- Order scan ------------------------------------------------
    if (isOrderBarcode(n) || (n < 10000 && n > 0)) {
      try {
        const r = await http.get(`/delivery/lookup-by-code/${n}`);
        const order = r.data;
        // Already in basket → ignore duplicate.
        if (basket.some((b) => b.id === order.id)) {
          toast.info(`Η παραγγελία #${padCode(order.code)} είναι ήδη στο καλάθι`);
          return;
        }
        // Pre-existing driver → ask the operator to confirm the change.
        if (order.driver_id) {
          setConflictOrder(order);
          return;
        }
        setBasket((prev) => [...prev, order]);
        toast.success(`#${padCode(order.code)} · ${order.customer_name || "—"} · ${fmtEUR(order.total || 0)}`);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Δεν βρέθηκε η παραγγελία");
      }
      return;
    }

    // -- Driver scan -----------------------------------------------
    if (isDriverBarcode(n)) {
      // Look up first — we need to know whether this driver is on a
      // ride before deciding between «return» vs «assign» flow.
      let driver;
      try {
        const r = await http.get(`/drivers/lookup-by-code/${n}`);
        driver = r.data;
      } catch (e) {
        toast.error(e.response?.data?.detail || "Δεν βρέθηκε ο οδηγός");
        return;
      }
      // Empty basket → either «επιστροφή οδηγού» or noop.
      if (basket.length === 0) {
        if (!driver.on_delivery) {
          toast.error("Σκανάρετε πρώτα μια παραγγελία");
          return;
        }
        try {
          const r = await http.post(`/drivers/${driver.id}/return`);
          const completed = r.data?.completed || [];
          toast.success(`Καλώς ήρθες πίσω ${driver.name}! · ${completed.length} παραδόσε${completed.length === 1 ? "ις" : "ις"}`, { duration: 5000 });
          try { onChanged?.(); } catch { /* noop */ }
          await reload();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Αποτυχία επιστροφής");
        }
        return;
      }
      // Non-empty basket → assign / extend ride.
      try {
        await http.post("/delivery/assign-driver-batch", {
          driver_id: driver.id,
          order_ids: basket.map((b) => b.id),
        });
        toast.success(`Ανατέθηκαν ${basket.length} παραγγελί${basket.length === 1 ? "α" : "ες"} σε: ${driver.name}`);
        setBasket([]);
        try { onChanged?.(); } catch { /* noop */ }
        onClose();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Αποτυχία ανάθεσης");
      }
      return;
    }

    toast.error("Μη αναγνωρίσιμο barcode (αναμένεται 7777XXXX ή 8888XXXX)");
  };

  // Confirm-change-driver flow.
  const acceptConflict = () => {
    setBasket((prev) => [...prev, { ...conflictOrder, _reassign: true }]);
    toast.success(`Προστέθηκε για επανανάθεση: #${padCode(conflictOrder.code)}`);
    setConflictOrder(null);
  };
  const declineConflict = () => setConflictOrder(null);

  const removeFromBasket = (id) => setBasket((prev) => prev.filter((b) => b.id !== id));
  const clearBasket = () => setBasket([]);

  // -- CRUD --------------------------------------------------------
  const saveDriver = async () => {
    if (!form.name.trim()) { toast.error("Όνομα οδηγού"); return; }
    setBusy(true);
    try {
      if (editing) {
        await http.put(`/drivers/${editing.id}`, form);
        toast.success("Αποθηκεύτηκε");
      } else {
        await http.post("/drivers", form);
        toast.success("Δημιουργήθηκε");
      }
      setEditing(null);
      setForm({ name: "", phone: "", active: true });
      await reload();
      try { onChanged?.(); } catch { /* noop */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία");
    } finally {
      setBusy(false);
    }
  };
  const startEdit = (d) => {
    setEditing(d);
    setForm({ name: d.name, phone: d.phone || "", active: d.active });
  };
  const delDriver = async (d) => {
    if (!(await confirmDialog(`Διαγραφή οδηγού «${d.name}»;`))) return;
    try {
      await http.delete(`/drivers/${d.id}`);
      toast.success("Διαγράφηκε");
      await reload();
      try { onChanged?.(); } catch { /* noop */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία");
    }
  };

  const onShiftDrivers = drivers.filter((d) => d.on_shift && d.active);
  const offShiftDrivers = drivers.filter((d) => !d.on_shift);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0">
        <DialogHeader className="px-5 py-3 bg-slate-50 border-b border-slate-200">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Truck size={20} /> Οδηγοί Διανομής
          </DialogTitle>
          <DialogDescription className="text-xs">
            Σκανάρετε παραγγελία (7777XXXX) και μετά κάρτα οδηγού (8888XXXX) για άμεση ανάθεση.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4 max-h-[75vh] overflow-y-auto space-y-4">
          {/* --- Active drivers ------------------------------------ */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider font-bold text-slate-600">
                Ενεργοί Οδηγοί ({onShiftDrivers.length})
              </div>
              <div className="text-[10px] text-slate-400">Κλικ για παραγγελίες</div>
            </div>
            {onShiftDrivers.length === 0 ? (
              <div className="text-center text-slate-400 py-3 text-sm italic">
                Κανένας οδηγός σε βάρδια. Ανοίξτε βάρδια από τη λίστα κάτω.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {onShiftDrivers.map((d) => {
                  const onDelivery = !!d.on_delivery;
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setDrillDriver(d)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border-2 text-left transition-colors ${
                        onDelivery
                          ? "border-amber-300 bg-amber-50 hover:bg-amber-100"
                          : "border-emerald-200 bg-emerald-50 hover:bg-emerald-100"
                      }`}
                      data-testid={`active-driver-${d.id}`}
                    >
                      <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${onDelivery ? "bg-amber-500 animate-pulse" : "bg-emerald-500"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold truncate">{d.name}</div>
                        <div className="text-[10px] font-mono text-slate-500 flex items-center gap-1.5">
                          <span>8888{padCode(d.code)}</span>
                          {onDelivery && d.ride_started_at && (
                            <RideTimer startedAt={d.ride_started_at} />
                          )}
                        </div>
                      </div>
                      {onDelivery ? (
                        <span className="text-[9px] uppercase font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded border border-amber-300">
                          Σε παράδοση
                        </span>
                      ) : (
                        <Eye size={14} className="text-slate-400" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* --- Scan / basket ------------------------------------- */}
          <div className="border-t-2 border-dashed border-slate-200 pt-3">
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-600 flex items-center gap-1.5">
              <ScanBarcode size={14} /> Σάρωση Barcode
            </Label>
            <Input
              ref={scanRef}
              value={scanValue}
              onChange={(e) => setScanValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleScan(scanValue);
                }
              }}
              placeholder="Σκανάρετε παραγγελία ή κάρτα οδηγού..."
              className="h-11 font-mono text-base mt-1.5"
              autoFocus
              data-testid="driver-hub-scan-input"
            />
            <div className="text-[10px] text-slate-400 mt-1">
              Παραγγελία: <span className="font-mono">7777XXXX</span> · Οδηγός: <span className="font-mono">8888XXXX</span>
            </div>

            {basket.length > 0 && (
              <div className="mt-3 rounded-md border-2 border-blue-200 bg-blue-50 p-2">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-xs font-bold text-blue-900">
                    Καλάθι Ανάθεσης ({basket.length})
                  </div>
                  <button
                    type="button"
                    onClick={clearBasket}
                    className="text-[10px] text-red-600 hover:underline"
                    data-testid="driver-hub-clear-basket"
                  >
                    Καθαρισμός
                  </button>
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {basket.map((o) => (
                    <div
                      key={o.id}
                      className="flex items-center gap-2 text-xs px-2 py-1 rounded bg-white border border-blue-200"
                      data-testid={`basket-item-${o.code}`}
                    >
                      <span className="font-mono font-bold text-blue-900">#{padCode(o.code)}</span>
                      <span className="flex-1 truncate">{o.customer_name || "—"}</span>
                      <span className="font-mono text-emerald-700 font-bold">{fmtEUR(o.total || 0)}</span>
                      {o._reassign && (
                        <span className="text-[9px] uppercase text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                          αλλαγή
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => removeFromBasket(o.id)}
                        className="text-slate-400 hover:text-red-600"
                      >
                        <XCircle size={13} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-blue-700 mt-1.5 italic">
                  Σκανάρετε κάρτα οδηγού (8888XXXX) για να ολοκληρώσετε την ανάθεση.
                </div>
              </div>
            )}
          </div>

          {/* --- All drivers (admin CRUD + shift toggle) ----------- */}
          <div className="border-t border-slate-200 pt-3">
            <div className="text-xs uppercase tracking-wider font-bold text-slate-600 mb-2">
              Μητρώο Οδηγών
            </div>
            {drivers.length === 0 ? (
              <div className="text-center text-slate-400 py-3 text-sm">Κανένας οδηγός</div>
            ) : (
              <div className="space-y-1.5">
                {drivers.map((d) => (
                  <div
                    key={d.id}
                    className="flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 bg-white"
                    data-testid={`driver-row-${d.id}`}
                  >
                    <div className={`w-2 h-2 rounded-full ${d.on_shift ? "bg-emerald-500" : d.active ? "bg-slate-300" : "bg-red-300"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate">{d.name}</div>
                      <div className="text-[10px] font-mono text-slate-500">
                        Κωδ.: <span className="font-bold">8888{padCode(d.code)}</span>
                        {d.phone ? <span className="ml-2">{d.phone}</span> : null}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant={d.on_shift ? "default" : "outline"}
                      onClick={() => toggleShift(d)}
                      disabled={busy || !d.active}
                      className={`h-8 px-2 text-[11px] ${d.on_shift ? "bg-red-600 hover:bg-red-700 text-white" : ""}`}
                      data-testid={`driver-toggle-shift-${d.id}`}
                    >
                      {d.on_shift ? (<><Square size={11} className="mr-1" /> Κλείσιμο</>) : (<><PlayCircle size={11} className="mr-1" /> Άνοιγμα</>)}
                    </Button>
                    {isAdmin && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => startEdit(d)} data-testid={`driver-edit-${d.id}`}>
                          <Pencil size={12} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => delDriver(d)} className="text-red-600" data-testid={`driver-del-${d.id}`}>
                          <Trash2 size={12} />
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* --- Admin form ---------------------------------------- */}
          {isAdmin && (
            <div className="border-t border-slate-200 pt-3 space-y-2 bg-slate-50 -mx-5 px-5 -mb-4 pb-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">
                {editing ? `Επεξεργασία: ${editing.name}` : "Νέος Οδηγός"}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Όνομα</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="h-9"
                    data-testid="driver-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Τηλέφωνο</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="h-9 font-mono"
                    data-testid="driver-phone"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch
                  checked={form.active}
                  onCheckedChange={(v) => setForm({ ...form, active: v })}
                  data-testid="driver-active"
                />
                Ενεργός (στο μητρώο)
              </label>
              <div className="flex gap-2 pt-1">
                <Button onClick={saveDriver} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 h-9 flex-1" data-testid="driver-save">
                  {editing ? (<><Check size={13} className="mr-1" /> Αποθήκευση</>) : (<><Plus size={13} className="mr-1" /> Δημιουργία</>)}
                </Button>
                {editing && (
                  <Button variant="outline" onClick={() => { setEditing(null); setForm({ name: "", phone: "", active: true }); }} className="h-9">
                    Άκυρο
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="px-5 py-3 border-t border-slate-200 bg-slate-50">
          <Button variant="outline" onClick={onClose} data-testid="driver-hub-close">Κλείσιμο</Button>
        </DialogFooter>
      </DialogContent>

      {/* Conflict prompt */}
      {conflictOrder && (
        <Dialog open onOpenChange={(v) => !v && declineConflict()}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700">
                <RotateCcw size={18} /> Αλλαγή Οδηγού;
              </DialogTitle>
              <DialogDescription>
                Η παραγγελία <span className="font-mono font-bold">#{padCode(conflictOrder.code)}</span> έχει ήδη ανατεθεί στον οδηγό
                {" "}<span className="font-bold">{conflictOrder.driver_name}</span>. Θέλετε να αλλάξετε την ανάθεση;
              </DialogDescription>
            </DialogHeader>
            <div className="bg-slate-50 rounded p-2 text-sm">
              <div>{conflictOrder.customer_name || "—"}</div>
              <div className="font-mono text-emerald-700 font-bold">{fmtEUR(conflictOrder.total || 0)}</div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={declineConflict} data-testid="conflict-no">
                <XCircle size={14} className="mr-1" /> Όχι
              </Button>
              <Button onClick={acceptConflict} className="bg-amber-600 hover:bg-amber-700" data-testid="conflict-yes">
                <CheckCircle2 size={14} className="mr-1" /> Ναι, αλλαγή
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Driver drilldown */}
      {drillDriver && (
        <DriverOrdersDrillDown
          driver={drillDriver}
          onClose={() => setDrillDriver(null)}
        />
      )}
    </Dialog>
  );
}

/** Read-only list of orders assigned to a driver since the current
 *  driver-shift opened (or last closed shift if none open). */
function DriverOrdersDrillDown({ driver, onClose }) {
  const [orders, setOrders] = useState([]);
  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await http.get(`/drivers/${driver.id}/orders`);
        if (!alive) return;
        setOrders(r.data?.orders || []);
        setShift(r.data?.shift || null);
      } catch {
        if (alive) setOrders([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [driver.id]);

  // Aggregated stats for the «debrief at end of day» card. All values
  // are derived purely from the orders array — no extra round-trip.
  //   • count          : total assigned orders this shift
  //   • totalSales     : sum of order.total
  //   • avgDeliveryMin : avg minutes between `created_at` and
  //                      `delivered_at`. We use `created_at` instead of
  //                      `dispatched_at` because dispatched_at is only
  //                      set for orders that went through the legacy
  //                      dispatch flow, while every order has a
  //                      `created_at`. NaN/null entries are skipped.
  //   • delivered     : count of orders that actually reached DELIVERED.
  const stats = (() => {
    const count = orders.length;
    const delivered = orders.filter((o) => o.status === "DELIVERED").length;
    const totalSales = orders.reduce((s, o) => s + Number(o.total || 0), 0);
    const durations = orders
      .map((o) => {
        // Prefer the persisted per-order duration (set when the driver
        // scans their card to return). Fallback to created→delivered_at
        // for legacy orders that pre-date the ride-tracking feature.
        if (Number.isFinite(o.delivery_duration_seconds)) {
          return Math.max(0, o.delivery_duration_seconds / 60);
        }
        if (!o.delivered_at || !o.created_at) return null;
        try {
          const t0 = new Date(o.created_at).getTime();
          const t1 = new Date(o.delivered_at).getTime();
          return Number.isFinite(t1 - t0) ? (t1 - t0) / 60000 : null;
        } catch { return null; }
      })
      // `v >= 0` defensively drops clock-skewed entries where
      // delivered_at < created_at (rare, but possible on a tablet
      // whose system clock drifted between the two writes).
      .filter((v) => v !== null && v >= 0);
    const avgDeliveryMin = durations.length
      ? durations.reduce((s, v) => s + v, 0) / durations.length
      : null;
    return { count, delivered, totalSales, avgDeliveryMin };
  })();

  const fmtMinutes = (m) => {
    if (m === null || m === undefined) return "—";
    if (m < 60) return `${Math.round(m)}′`;
    const h = Math.floor(m / 60);
    const r = Math.round(m % 60);
    return `${h}ω ${r}′`;
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck size={18} /> Παραγγελίες — {driver.name}
          </DialogTitle>
          <DialogDescription>
            {shift?.opened_at
              ? `Βάρδια από ${new Date(shift.opened_at).toLocaleString("el-GR")}`
              : "Δεν υπάρχει ενεργή βάρδια"}
            {" · "}<span className="font-mono">8888{padCode(driver.code)}</span>
          </DialogDescription>
        </DialogHeader>

        {!loading && orders.length > 0 && (
          <div className="grid grid-cols-3 gap-2 text-center" data-testid="driver-stats">
            <div className="rounded-lg border-2 border-blue-200 bg-blue-50 p-2.5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-blue-700">
                Παραγγελίες
              </div>
              <div className="text-2xl font-bold font-mono text-blue-900 leading-tight" data-testid="stat-count">
                {stats.count}
              </div>
              <div className="text-[10px] text-blue-700 mt-0.5">
                {stats.delivered} παραδόθηκαν
              </div>
            </div>
            <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-2.5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">
                Σύνολο Πωλήσεων
              </div>
              <div className="text-2xl font-bold font-mono text-emerald-900 leading-tight" data-testid="stat-sales">
                {fmtEUR(stats.totalSales)}
              </div>
              <div className="text-[10px] text-emerald-700 mt-0.5">
                {stats.count > 0 ? `μ.ο. ${fmtEUR(stats.totalSales / stats.count)}` : "—"}
              </div>
            </div>
            <div className="rounded-lg border-2 border-amber-200 bg-amber-50 p-2.5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">
                Μ.Ο. Παράδοσης
              </div>
              <div className="text-2xl font-bold font-mono text-amber-900 leading-tight" data-testid="stat-avg-time">
                {fmtMinutes(stats.avgDeliveryMin)}
              </div>
              <div className="text-[10px] text-amber-700 mt-0.5">
                από καταχώρηση
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-4 text-sm text-slate-400">Φόρτωση...</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-4 text-sm text-slate-400">
            Καμία παραγγελία αυτήν την βάρδια.
          </div>
        ) : (
          <>
            <div className="max-h-[50vh] overflow-y-auto space-y-1">
              {orders.map((o) => (
                <div
                  key={o.id}
                  className="flex items-center gap-2 px-3 py-2 rounded border border-slate-200 bg-white text-sm"
                  data-testid={`drill-order-${o.code}`}
                >
                  <span className="font-mono font-bold">#{padCode(o.code)}</span>
                  <span className="flex-1 truncate">{o.customer_name || "—"}</span>
                  <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded font-bold ${
                    o.status === "DELIVERED"
                      ? "bg-emerald-100 text-emerald-800"
                      : o.status === "DISPATCHED"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-slate-100 text-slate-700"
                  }`}>{o.status}</span>
                  <span className="font-mono font-bold text-emerald-700">{fmtEUR(o.total || 0)}</span>
                </div>
              ))}
            </div>
            <div className="border-t pt-2 flex justify-between text-base font-bold">
              <span>Σύνολο</span>
              <span className="font-mono text-emerald-700">{fmtEUR(stats.totalSales)}</span>
            </div>
          </>
        )}
        <DialogFooter className="gap-2">
          {!loading && orders.length > 0 && (
            <Button
              variant="outline"
              onClick={() => printDriverShiftReport({ driver, shift, orders })}
              className="bg-blue-50 border-blue-300 text-blue-800 hover:bg-blue-100"
              data-testid="drill-print-report"
            >
              <Printer size={14} className="mr-1.5" /> Αναφορά Βάρδιας
            </Button>
          )}
          <Button variant="outline" onClick={onClose} data-testid="drill-close">Κλείσιμο</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/** Live-updating ride elapsed-time chip. Re-renders every 15s so the
 *  «Σε παράδοση» badge stays roughly accurate without thrashing the
 *  browser timer wheel. Format: `12′` if <60min, `1ω 5′` otherwise. */
function RideTimer({ startedAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);
  const ms = now - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const min = Math.floor(ms / 60000);
  const label = min < 60 ? `${min}′` : `${Math.floor(min / 60)}ω ${min % 60}′`;
  return (
    <span className="text-amber-700 font-bold" data-testid="ride-timer">
      · {label}
    </span>
  );
}
